import type { ReactNode } from "react";
import { Link } from "wouter";
import {
  Activity,
  AlertTriangle,
  Bell,
  Camera,
  CreditCard,
  Globe,
  LayoutDashboard,
  LogOut,
  Server,
  Shield,
  Users,
} from "lucide-react";
import { adminSectionHref } from "@/lib/admin-navigation";
import type { AdminSection } from "@/lib/admin-navigation";

export type { AdminSection } from "@/lib/admin-navigation";

interface AdminNavProps {
  /** Which destination is currently selected. */
  activeSection: AdminSection | "user-detail";
  /** Optional compatibility callback for controlled admin shells. */
  onSectionChange?: (section: AdminSection) => void;
  onLogout: () => void;
}

interface NavItem {
  id: AdminSection;
  label: string;
  compactLabel: string;
  hint: string;
  icon: ReactNode;
}

const NAV_ITEMS: NavItem[] = [
  { id: "dashboard", label: "Home", compactLabel: "Home", hint: "Daily operating view", icon: <LayoutDashboard aria-hidden="true" /> },
  { id: "billing", label: "Billing", compactLabel: "Billing", hint: "Plans and payments", icon: <CreditCard aria-hidden="true" /> },
  { id: "alerts", label: "Alert testing", compactLabel: "Alerts", hint: "Notification checks", icon: <Bell aria-hidden="true" /> },
  { id: "users", label: "User database", compactLabel: "Users", hint: "Accounts and access", icon: <Users aria-hidden="true" /> },
  { id: "errors", label: "Error logs", compactLabel: "Errors", hint: "Service diagnostics", icon: <AlertTriangle aria-hidden="true" /> },
  { id: "surfspots", label: "Surf spots", compactLabel: "Spots", hint: "Forecast locations", icon: <Globe aria-hidden="true" /> },
  { id: "webcams", label: "Webcam reviews", compactLabel: "Cameras", hint: "Community camera queue", icon: <Camera aria-hidden="true" /> },
  { id: "system", label: "System", compactLabel: "System", hint: "Integrations and health", icon: <Server aria-hidden="true" /> },
];

export default function AdminNav({ activeSection, onSectionChange, onLogout }: AdminNavProps) {
  const isActive = (section: AdminSection) =>
    section === activeSection || (section === "users" && activeSection === "user-detail");

  const renderLink = (item: NavItem, compact = false) => (
    <Link
      key={item.id}
      href={adminSectionHref(item.id)}
      aria-current={isActive(item.id) ? "page" : undefined}
      aria-label={compact ? `${item.label}: ${item.hint}` : undefined}
      onClick={(event) => {
        if (onSectionChange && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
          event.preventDefault();
          onSectionChange(item.id);
        }
      }}
      className={`group flex min-w-0 items-center rounded-xl transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${
        compact
          ? "min-h-14 flex-col justify-center gap-1 px-1.5 py-2 text-[11px] leading-none"
          : "gap-3 px-3 py-2.5 text-sm"
      } ${
        isActive(item.id)
          ? "bg-emerald-400/15 text-emerald-300 ring-1 ring-inset ring-emerald-300/20"
          : "text-slate-300 hover:bg-white/[0.06] hover:text-white"
      }`}
    >
      <span className={`grid shrink-0 place-items-center ${compact ? "h-5 w-5" : "h-5 w-5"}`}>
        {item.icon}
      </span>
      <span className={compact ? "max-w-full truncate" : "truncate"}>{compact ? item.compactLabel : item.label}</span>
      {!compact && (
        <span className={`ml-auto h-1.5 w-1.5 rounded-full bg-emerald-300 transition-opacity ${isActive(item.id) ? "opacity-100" : "opacity-0"}`} />
      )}
    </Link>
  );

  return (
    <>
      <aside
        aria-label="Admin navigation"
        className="fixed inset-y-3 left-3 z-30 hidden w-60 flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#071525]/95 p-2.5 text-slate-100 shadow-[0_18px_50px_rgba(0,0,0,.32)] backdrop-blur md:flex"
      >
        <div className="flex shrink-0 items-center gap-3 border-b border-white/10 px-3 py-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-emerald-300/20 bg-emerald-300/10 text-emerald-300">
            <Shield aria-hidden="true" className="h-[18px] w-[18px]" />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold tracking-tight">LiveSwell</span>
            <span className="mt-0.5 block text-[10px] font-medium uppercase tracking-[0.17em] text-slate-400">Operations</span>
          </span>
          <span aria-hidden="true" className="ml-auto flex h-2 w-2 shrink-0">
            <span className="h-2 w-2 rounded-full bg-emerald-300" />
          </span>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto py-3">
          <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Workspace</p>
          <nav aria-label="Admin sections" className="space-y-1">
            {NAV_ITEMS.map((item) => renderLink(item))}
          </nav>
        </div>

        <div className="shrink-0 border-t border-white/10 pt-2">
          <div className="mb-2 flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] text-slate-500">
            <Activity aria-hidden="true" className="h-3.5 w-3.5 text-emerald-300" />
            Service console
          </div>
          <button
            type="button"
            onClick={onLogout}
            data-testid="button-admin-logout"
            className="flex min-h-10 w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm text-slate-300 transition-colors hover:bg-rose-400/10 hover:text-rose-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
          >
            <LogOut aria-hidden="true" className="h-5 w-5" />
            Sign out
          </button>
        </div>
      </aside>

      <nav
        aria-label="Admin navigation"
        className="mx-3 mb-3 grid grid-cols-4 gap-1 rounded-2xl border border-white/10 bg-[#071525]/95 p-2 text-slate-100 shadow-lg backdrop-blur md:hidden"
      >
        {NAV_ITEMS.map((item) => renderLink(item, true))}
        <button
          type="button"
          onClick={onLogout}
          data-testid="button-admin-logout-mobile"
          className="flex min-h-14 min-w-0 flex-col items-center justify-center gap-1 rounded-xl px-1.5 py-2 text-[11px] leading-none text-slate-300 transition-colors hover:bg-rose-400/10 hover:text-rose-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
        >
          <LogOut aria-hidden="true" className="h-5 w-5" />
          <span>Sign out</span>
        </button>
      </nav>
    </>
  );
}
